/**
 * Thân một tin, trình bày theo khối: mỗi khoản một dòng, mã đơn in đậm, tin dài
 * thu gọn kèm nút "Xem cả N dòng". Chuông và trang "Thông báo của tôi" dùng
 * chung (CEO chốt 1a · 3a, 31/10/2026).
 */

import { useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import {
  ORDER_CODE,
  parseNotificationBody,
  sectionTotal,
  type BodyBlock,
} from "./notification-body";

/** Tin có tới mức này khoản thì hiện đủ luôn, khỏi bắt bấm. */
const SHOW_ALL_UP_TO = 3;
/** Thu gọn một nhóm: chừa bấy nhiêu khoản đầu. */
const PREVIEW_ITEMS = 2;

function withBoldCodes(line: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of line.matchAll(ORDER_CODE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(line.slice(last, at));
    out.push(
      <span key={at} className="whitespace-nowrap font-medium text-foreground">
        {m[0]}
      </span>,
    );
    last = at + m[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

function Items({ items }: { items: string[] }) {
  return (
    <ul className="mt-1 space-y-1 border-l-2 border-border pl-2">
      {items.map((it, i) => (
        <li key={i} className="break-words text-xs leading-snug text-muted-foreground">
          {withBoldCodes(it)}
        </li>
      ))}
    </ul>
  );
}

function SectionHead({ title, total }: { title: string; total: number }) {
  if (!title) return null;
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
      <span className="min-w-0 break-words">{title}</span>
      <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground">
        {total}
      </span>
    </div>
  );
}

export function NotificationBody({
  body,
  collapsible = true,
  className = "",
}: {
  body: string;
  /** Tắt = luôn hiện đủ (bài kiểm, chỗ đã có không gian). */
  collapsible?: boolean;
  className?: string;
}) {
  const blocks = useMemo(() => parseNotificationBody(body), [body]);
  const [open, setOpen] = useState(false);
  if (blocks.length === 0) return null;

  const sections = blocks.filter(
    (b): b is Extract<BodyBlock, { kind: "section" }> => b.kind === "section",
  );
  const shownItems = sections.reduce((n, s) => n + s.items.length, 0);
  const total = sections.reduce((n, s) => n + sectionTotal(s), 0);
  const folds = collapsible && shownItems > SHOW_ALL_UP_TO;
  const collapsed = folds && !open;

  // Nút nằm trong dòng bấm-để-mở-tin: chặn nổi lên, không thì bấm "Xem" là mở tin.
  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <div className={`mt-0.5 ${className}`}>
      {blocks.map((b, i) => {
        if (b.kind === "text") {
          return (
            <p
              key={i}
              className={
                "whitespace-pre-line break-words text-xs text-muted-foreground" +
                (collapsed ? " line-clamp-3" : "")
              }
            >
              {withBoldCodes(b.text)}
            </p>
          );
        }
        const n = sectionTotal(b);
        if (collapsed && sections.length > 1) {
          // Nhiều nhóm: thu gọn còn dòng đầu nhóm + số — nhìn là biết có những việc gì.
          return <SectionHead key={i} title={b.title || "Chi tiết"} total={n} />;
        }
        const items = collapsed ? b.items.slice(0, PREVIEW_ITEMS) : b.items;
        const cut = collapsed ? 0 : (b.more ?? 0);
        return (
          <div key={i}>
            <SectionHead title={b.title} total={n} />
            <Items items={items} />
            {cut > 0 ? (
              <p className="mt-1 pl-2.5 text-[11px] text-muted-foreground">
                Còn {cut} dòng nữa không kèm trong tin.
              </p>
            ) : null}
          </div>
        );
      })}
      {folds ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="mt-1 inline-flex min-h-[var(--touch-min)] items-center gap-0.5 text-xs font-medium text-primary hover:underline md:min-h-0"
        >
          {open ? (
            <>
              Thu gọn <ChevronUp className="h-3.5 w-3.5" aria-hidden />
            </>
          ) : (
            <>
              {sections.length > 1 ? "Xem chi tiết" : `Xem cả ${total} dòng`}
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}
